/**
 * 통합 미리보기 컴포넌트 (반응형)
 * 메인 페이지에서 최신 게시물(공지사항, 포토갤러리)을 미리보기 형태로 표시합니다.
 * 일반 목록 형태와 이미지 썸네일 형태를 지원합니다.
 * 데스크톱과 모바일 모두에서 사용 가능한 반응형 컴포넌트입니다.
 */

import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import useAsync from '../hooks/useAsync';
import logoPreview from '../assets/logo_preview.png';
import { Sort } from '../utils/Sort';
import { useIsMobile } from '../hooks/useMediaQuery';

// 한 번에 가져올 게시물 수
const LIMIT = 6;

/**
 * 날짜를 "YYYY. MM. DD" 형식으로 포맷팅하는 함수
 * @param {string|Date} value - 날짜 값
 * @returns {string} 포맷된 날짜 문자열
 */
function formatDate(value) {
  const date = new Date(value);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}. ${month}. ${day}`;
}

/**
 * 일반 목록 형태의 게시물 항목 컴포넌트
 * 제목과 날짜를 표시합니다.
 * @param {Object} props
 * @param {Object} props.item - 게시물 데이터
 * @param {string} props.order - 상세 페이지 경로
 */
function ItemListItem({ item, order }) {
  return (
    <ListItemRow>
      <ItemLink to={order + '/' + item.id}>{item.title}</ItemLink>
      <DateSpan>{formatDate(item.createdAt)}</DateSpan>
    </ListItemRow>
  );
}

/**
 * 이미지 썸네일 형태의 게시물 항목 컴포넌트
 * 포토갤러리 미리보기에 사용됩니다.
 * @param {Object} props
 * @param {Object} props.item - 게시물 데이터
 * @param {string} props.order - 상세 페이지 경로
 */
function ImageListItem({ item, order }) {
  return (
    <PhotoItem>
      <Link to={order + '/' + item.id}>
        <PhotoThumb
          $hasImage={!!item.thumb_files}
          $imageUrl={item.thumb_files}
        />
      </Link>
      <PhotoTitle to={order + '/' + item.id}>{item.title}</PhotoTitle>
    </PhotoItem>
  );
}

/**
 * Preview 메인 컴포넌트
 * @param {Object} props
 * @param {string} props.address - 목록 페이지 경로
 * @param {Function} props.getdata - 데이터를 가져오는 API 함수 (getNotices, getPhotos 등)
 * @param {boolean} props.isPhoto - 포토갤러리 여부 (true: 이미지 형태, false: 목록 형태)
 */
function Preview({ address, getdata, isPhoto }) {
  // 비동기 API 호출을 위한 커스텀 훅
  const [, , getNoticessAsync] = useAsync(getdata);

  // 게시물 목록 데이터
  const [items, setItems] = useState([]);

  // 모바일 여부 확인
  const isMobile = useIsMobile();

  // ID 기준 내림차순 정렬 후 공개 여부로 정렬
  const sortedItems = Sort(items.sort((a, b) => b['id'] - a['id']));

  /**
   * 게시물 데이터를 로드하는 핸들러
   * @param {Object} options - API 호출 옵션 (order, offset, limit)
   */
  const handleLoad = useCallback(
    async (options) => {
      const result = await getNoticessAsync(options);
      if (!result) return;

      const datas = result;

      // offset이 0이면 새로 로드, 아니면 기존 목록에 추가
      if (options.offset === 0) {
        setItems(datas);
      } else {
        setItems((prevItems) => [...prevItems, ...datas]);
      }
    },
    [getNoticessAsync]
  );

  /**
   * 컴포넌트 마운트 시 데이터 로드
   */
  useEffect(() => {
    handleLoad({ order: 'createdAt', offset: 0, limit: LIMIT });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 모바일: 3개, 데스크톱: 5개
  const itemLimit = isMobile && !isPhoto ? 3 : isPhoto ? 2 : 5;

  return (
    <PreviewContainer>
      {/* 일반 목록 형태 (공지사항 등) */}
      {!isPhoto && (
        <>
          {Object.keys(sortedItems).map((key, index) => {
            if (index < itemLimit) {
              return (
                <ListItem key={sortedItems[key].id}>
                  <ItemListItem item={sortedItems[key]} order={address} />
                </ListItem>
              );
            }
            return null;
          })}
          {/* 모바일에서만 표시되는 더보기 버튼 */}
          {isMobile && (
            <MoreButtonWrapper>
              <MoreButton to={address}>더보기</MoreButton>
            </MoreButtonWrapper>
          )}
        </>
      )}

      {/* 이미지 썸네일 형태 (포토갤러리) */}
      {isPhoto && (
        <PhotoGrid>
          {Object.keys(sortedItems).map((key, index) => {
            if (index < 2) {
              return (
                <ImageListItem
                  key={sortedItems[key].id}
                  item={sortedItems[key]}
                  order={address}
                />
              );
            }
            return null;
          })}
        </PhotoGrid>
      )}
    </PreviewContainer>
  );
}

export default Preview;

/* Styled Components */

const PreviewContainer = styled.ul`
  /* 데스크톱 기본 스타일 */
  list-style: none;
  margin: 0;
  padding: 0;

  /* 모바일 스타일 */
  @media (max-width: 1023px) {
    padding: 0 5%;
  }
`;

const ListItem = styled.li`
  /* 데스크톱 기본 스타일 */
  position: relative;
  margin: 0 0 13px 15px !important;
  list-style: disc;
  padding-left: 15px;

  /* 모바일 스타일 */
  @media (max-width: 1023px) {
    margin: 0 0 10px 0 !important;
    list-style: none;
    padding-left: 0;
    padding-bottom: 10px;
    border-bottom: 1px solid #e0e0e0;

    &:last-of-type {
      border-bottom: none;
    }
  }
`;

const ListItemRow = styled.div`
  /* 데스크톱 기본 스타일 */
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  width: 100%;
  gap: 10px;

  /* 모바일 스타일 */
  @media (max-width: 1023px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;
  }
`;

const ItemLink = styled(Link)`
  /* 데스크톱 기본 스타일 */
  font-size: 14px;
  display: block;
  max-width: 300px;
  color: #474747;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  word-break: break-all;
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  font-weight: normal;
  letter-spacing: normal;

  &:hover,
  &:active,
  &:focus {
    color: #000;
    text-decoration: underline;
  }

  /* 모바일 스타일 */
  @media (max-width: 1023px) {
    max-width: 100%;
    font-size: 13px;
    line-height: 1.5;
  }
`;

const DateSpan = styled.span`
  /* 데스크톱 기본 스타일 */
  color: #7e7e7e;
  font-family: 'koPubWorld DotumM';
  font-size: 14px;
  margin-left: 10px;
  flex-shrink: 0;

  /* 모바일 스타일 */
  @media (max-width: 1023px) {
    margin-left: 0;
    font-size: 12px;
  }
`;

const MoreButtonWrapper = styled.div`
  /* 모바일 전용 - 데스크톱에서는 표시 안 됨 */
  text-align: center;
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px solid #e0e0e0;
`;

const MoreButton = styled(Link)`
  display: inline-block;
  padding: 10px 30px;
  background-color: ${(props) => props.theme.colors.primary};
  color: ${(props) => props.theme.colors.white};
  font-family: ${(props) => props.theme.fonts.bold};
  font-size: 14px;
  text-decoration: none;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover,
  &:active,
  &:focus {
    background-color: ${(props) => props.theme.colors.primaryHover};
  }
`;

const PhotoGrid = styled.div`
  /* 데스크톱 기본 스타일 */
  display: flex;
  gap: 20px;
  justify-content: flex-start;

  /* 모바일 스타일 */
  @media (max-width: 767px) {
    flex-direction: column;
    gap: 15px;
    padding: 0 5%;
  }
`;

const PhotoItem = styled.li`
  /* 데스크톱 기본 스타일 */
  width: 223px;
  list-style: none;
  margin: 0;
  padding: 0;

  /* 모바일 스타일 */
  @media (max-width: 767px) {
    width: 100%;
  }
`;

const PhotoThumb = styled.div`
  /* 데스크톱 기본 스타일 */
  width: 221px;
  height: 133px;
  border: 1px solid #b2b2b2;
  background: ${(props) =>
    props.$hasImage
      ? `#f4f4f4 url('${props.$imageUrl}') center center no-repeat`
      : `#f4f4f4 url(${logoPreview}) 50% 50% no-repeat`};
  background-size: ${(props) => (props.$hasImage ? 'cover' : 'auto')};

  /* 모바일 스타일 */
  @media (max-width: 767px) {
    width: 100%;
    height: 200px;
  }
`;

const PhotoTitle = styled(Link)`
  /* 데스크톱 기본 스타일 */
  display: block;
  color: #333333;
  width: 221px;
  margin-top: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: normal;
  white-space: nowrap;
  word-break: break-all;
  font-size: 14px;

  &:hover,
  &:active,
  &:focus {
    color: #000;
    text-decoration: underline;
  }

  /* 모바일 스타일 */
  @media (max-width: 767px) {
    width: 100%;
    font-size: 13px;
  }
`;
